import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import bgImage from '../images/background.png';
import mugBg from '../images/user-background.png';
import { PROFILE_IMAGES, CHAR_SCALES } from '../constants/profileImages';

function Icon({ name, size = 20 }) {
    const props = { width: size, height: size, viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: 1.5, strokeLinecap: "round", strokeLinejoin: "round" };
    switch (name) {
        case 'home':
            return <svg {...props}><path d="M3 11l9-8 9 8" /><path d="M5 10v10h14V10" /><path d="M10 20v-6h4v6" /></svg>;
        case 'back':
            return <svg {...props}><path d="M15 18l-6-6 6-6" /></svg>;
        case 'stats':
            return <svg {...props}><path d="M3 3v18h18" /><path d="M7 14l4-4 4 4 5-5" /></svg>;
        case 'edit':
            return <svg {...props}><path d="M12 20h9" /><path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4 12.5-12.5z" /></svg>;
        case 'check':
            return <svg {...props}><path d="M20 6L9 17l-5-5" /></svg>;
        case 'x':
            return <svg {...props}><path d="M18 6L6 18" /><path d="M6 6l12 12" /></svg>;
        case 'logout':
            return <svg {...props}><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" /><path d="M16 17l5-5-5-5" /><path d="M21 12H9" /></svg>;
        default: return null;
    }
}

const PerfilComponent = () => {
    const { t } = useTranslation();
    const [profileInfo, setProfileInfo] = useState({ username: '', average: 0.00, wines: 0, profile: 0, id: 0, ranking: 0 });
    const [userData, setUserData] = useState({ profile: 0, average: 0.00, username: '', ranking: 0, wines: 0, victories: 0, winegangs: [], id: 0 });
    const [ready, setReady] = useState(false);
    const [editMode, setEditMode] = useState(false);
    const [selectedIdx, setSelectedIdx] = useState(0);
    const [visibleIdx, setVisibleIdx] = useState(0);
    const [animClass, setAnimClass] = useState('');
    const [saving, setSaving] = useState(false);
    const [fetchError, setFetchError] = useState(false);
    const [retryKey, setRetryKey] = useState(0);
    const animLock = useRef(false);
    const navigate = useNavigate();
    const { id } = useParams();

    const profiles = t('profiles', { returnObjects: true });

    useEffect(() => {
        const storedUserInfo = localStorage.getItem('userLogged');
        if (storedUserInfo) {
            const userInfo = JSON.parse(storedUserInfo);
            setProfileInfo({
                username: userInfo.username,
                average: userInfo.average || 0.00,
                wines: userInfo.wines || 0,
                id: userInfo.id || 0,
                ranking: userInfo.ranking || 0,
                profile: userInfo.profile || 0,
            });
        }
    }, []);

    useEffect(() => {
        setFetchError(false);
        const xhr = new XMLHttpRequest();
        xhr.open("GET", `${process.env.REACT_APP_API_URL}/user/${id}`, true);
        xhr.onreadystatechange = function () {
            if (this.readyState === 4) {
                if (this.status === 200) {
                    const data = JSON.parse(this.responseText);
                    setUserData(data);
                    setSelectedIdx(data.profile || 0);
                    setVisibleIdx(data.profile || 0);
                    requestAnimationFrame(() => setReady(true));
                } else {
                    setFetchError(true);
                }
            }
        };
        xhr.send();
    }, [id, retryKey]);

    function clearLocalStorage() {
        localStorage.clear();
        navigate('/login');
    }

    const animateChange = useCallback((nextIdx, dir) => {
        if (animLock.current) return;
        animLock.current = true;

        const exitClass = dir >= 0 ? 'up-char-exit-left' : 'up-char-exit-right';
        const enterClass = dir >= 0 ? 'up-char-enter-right' : 'up-char-enter-left';

        setAnimClass(exitClass);
        setTimeout(() => {
            // Render A: scale+image change while exit fill-mode still hides (opacity 0)
            setVisibleIdx(nextIdx);
            setSelectedIdx(nextIdx);
            // Render B (next frame): start enter animation once new image is in DOM
            requestAnimationFrame(() => {
                setAnimClass(enterClass);
            });
            setTimeout(() => {
                setAnimClass('');
                animLock.current = false;
            }, 420);
        }, 180);
    }, []);

    const cycleCharacter = (dir) => {
        setSelectedIdx(prev => {
            let next = prev + dir * 2;
            if (next < 0) next = (next + 14) % 14;
            if (next >= 14) next = next % 14;
            animateChange(next, dir);
            return prev;
        });
    };

    const setGender = (female) => {
        setSelectedIdx(prev => {
            const base = Math.floor(prev / 2) * 2;
            const next = female ? base + 1 : base;
            animateChange(next, female ? 1 : -1);
            return prev;
        });
    };

    const handleConfirm = () => {
        setSaving(true);
        const xhr = new XMLHttpRequest();
        const data = new FormData();
        data.append('profile', selectedIdx);
        xhr.open("POST", `${process.env.REACT_APP_API_URL}/users/${profileInfo.id}`, true);
        xhr.onreadystatechange = function () {
            if (this.readyState === 4) {
                setSaving(false);
                if (this.status === 200) {
                    const user = JSON.parse(this.responseText);
                    const updated = { ...profileInfo, profile: user.profile };
                    localStorage.setItem('userLogged', JSON.stringify(updated));
                    setUserData(prev => ({ ...prev, profile: user.profile }));
                    setProfileInfo(updated);
                }
                setEditMode(false);
            }
        };
        xhr.send(data);
    };

    const handleCancelEdit = () => {
        setSelectedIdx(userData.profile || 0);
        setEditMode(false);
    };

    const isOwn = userData.id === profileInfo.id;
    const displayIdx = editMode ? visibleIdx : (userData.profile || 0);
    const isFemale = selectedIdx % 2 !== 0;
    const characterNo = Math.floor(selectedIdx / 2) + 1;
    const fileNo = String(100000 + (userData.id || 0)).slice(1);
    const profileData = Array.isArray(profiles) ? profiles[displayIdx] : null;

    return (
        <div className={`up-stage${ready ? ' up-ready' : ''}`}>
            <div className="up-stage-bg" style={{ backgroundImage: `url(${bgImage})` }} />
            <div className="up-vignette" />
            <div className="up-grain" />

            {fetchError && (
                <div className="page-fetch-error">
                    <p>{t('common.fetch_error')}</p>
                    <button onClick={() => { setFetchError(false); setRetryKey(k => k + 1); }}>{t('common.retry')}</button>
                </div>
            )}

            <div className="up-topbar">
                <div className="up-nav-group">
                    <Link to="/" className="up-icon-btn" title={t('common.home')}><Icon name="home" /></Link>
                    <button className="up-icon-btn" onClick={() => navigate(-1)} title={t('common.back')}><Icon name="back" /></button>
                </div>
                <div className="up-topbar-title">
                    <small>{editMode ? t('edit_user.title') : t('user.profile_title')}</small>
                </div>
                <div className="up-nav-group" style={{ visibility: 'hidden' }}>
                    <span className="up-icon-btn" />
                    <span className="up-icon-btn" />
                </div>
            </div>

            <div className="up-shell">
                <div className="up-portrait-col">
                    <div className="up-portrait-wrap">
                        <div className="up-portrait-frame" style={{ backgroundImage: `url(${mugBg})` }}>
                            <div className={`up-char-slide${animClass ? ' ' + animClass : ''}`}>
                                <div style={{
                                    position: 'absolute', inset: 0,
                                    transform: `scale(${CHAR_SCALES[displayIdx]})`,
                                    transformOrigin: 'bottom center',
                                }}>
                                    <img src={PROFILE_IMAGES[displayIdx]} alt="" className="up-portrait-img" />
                                </div>
                            </div>
                            <div className="up-portrait-tape">EVIDENCE · WPD</div>
                            <div className="up-portrait-id">
                                <span>FILE Nº {fileNo}</span>
                                <span>CASE · WINE TASTING</span>
                            </div>
                        </div>

                        <div className={`up-portrait-controls${editMode ? ' up-controls-active' : ''}`}>
                            <div className="up-character-counter">{t('edit_user.character_counter', { number: characterNo })}</div>
                            <button className="up-arrow-btn up-arrow-left" onClick={() => cycleCharacter(-1)}>
                                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M15 18l-6-6 6-6" /></svg>
                            </button>
                            <button className="up-arrow-btn up-arrow-right" onClick={() => cycleCharacter(1)}>
                                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M9 18l6-6-6-6" /></svg>
                            </button>
                            <div className="up-gender-buttons">
                                <button className={`up-gender-btn${!isFemale ? ' up-gender-active' : ''}`} onClick={() => setGender(false)} title={t('edit_user.male')}>
                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><circle cx="10" cy="14" r="5" /><path d="M19 5l-5.5 5.5" /><path d="M14 5h5v5" /></svg>
                                </button>
                                <button className={`up-gender-btn${isFemale ? ' up-gender-active' : ''}`} onClick={() => setGender(true)} title={t('edit_user.female')}>
                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="9" r="5" /><path d="M12 14v7" /><path d="M9 18h6" /></svg>
                                </button>
                            </div>
                        </div>
                    </div>

                    <div className="up-portrait-meta">
                        <span className="up-badge"><span className="up-dot" />{t('common.wine_police')}</span>
                        <h1>{userData.username}</h1>
                        {profileData && <p>"{profileData.title}"</p>}
                    </div>
                </div>

                <div className="up-info-col">
                    {!editMode && (
                        <>
                            {profileData && profileData.description && (
                                <div className="up-panel up-panel-story">
                                    <div className="up-panel-eyebrow">{t('user.story')}</div>
                                    <p className="up-story-text">{profileData.description}</p>
                                </div>
                            )}

                            <div className="up-panel">
                                <div className="up-panel-eyebrow">{t('user.summary')}</div>
                                <div className="up-stat-grid">
                                    <div className="up-stat">
                                        <div className="up-stat-label">{t('user.ranking_month_short')}</div>
                                        <div className="up-stat-value">#{userData.ranking}</div>
                                    </div>
                                    <div className="up-stat">
                                        <div className="up-stat-label">{t('user.level').replace(':', '').replace(' :', '')}</div>
                                        <div className="up-stat-value">{parseFloat(userData.average || 0).toFixed(1)}<span className="up-stat-unit">%</span></div>
                                    </div>
                                    <div className="up-stat">
                                        <div className="up-stat-label">{t('user.stat_wines_tasted')}</div>
                                        <div className="up-stat-value">{userData.wines}</div>
                                    </div>
                                </div>
                                {userData.victories > 0 && (
                                    <div className="up-trophies-row">
                                        <div className="up-trophy-coin">{userData.victories}</div>
                                        <div className="up-trophies-meta">
                                            <span className="up-trophies-label">{t('user.stat_trophies')}</span>
                                            <span className="up-trophies-text">{t('user.stat_monthly_wins')}</span>
                                        </div>
                                    </div>
                                )}
                            </div>

                            {userData.winegangs && (
                                <div className="up-panel">
                                    <div className="up-panel-eyebrow">{t('user.winegang').replace(':', '')}</div>
                                    <div className="up-gangs-list">
                                        {userData.winegangs.length === 0 && (
                                            <div className="up-empty-row">{t('user.no_winegang')}</div>
                                        )}
                                        {userData.winegangs.map(g => (
                                            <div key={g.id} className="up-gang-row">
                                                <span className="up-gang-name">{g.winegang_name}</span>
                                                <span className={`up-gang-role${g.role === 'Líder' ? ' up-gang-lider' : ''}`}>{g.role}</span>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </>
                    )}


                    <div className="up-actions">
                        {!editMode && (
                            <Link to={`/stats/${userData.id}`} className="up-action-btn up-action-primary">
                                <Icon name="stats" size={16} /> {t('common.stats')}
                            </Link>
                        )}
                        {isOwn && !editMode && (
                            <button className="up-action-btn" onClick={() => setEditMode(true)}>
                                <Icon name="edit" size={16} /> {t('common.edit')}
                            </button>
                        )}
                        {isOwn && editMode && (
                            <>
                                <button className="up-action-btn up-action-primary" onClick={handleConfirm} disabled={saving}>
                                    <Icon name="check" size={16} /> {saving ? t('common.loading') : t('common.confirm')}
                                </button>
                                <button className="up-action-btn" onClick={handleCancelEdit}>
                                    <Icon name="x" size={16} /> {t('common.cancel')}
                                </button>
                            </>
                        )}
                        <span className="up-action-spacer" />
                        {isOwn && !editMode && (
                            <button className="up-action-btn up-action-danger" onClick={clearLocalStorage}>
                                <Icon name="logout" size={16} /> {t('user.logout')}
                            </button>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default PerfilComponent;
